import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { groupTaskListQuery } from "./groupTaskListQuery";
import { IGroupSpaceResource } from "../../group-spaces";
import { useApi } from "../../SDKProvider";
import { IUserResource } from "../../users";
import { unwrapAPIResult } from "../../utils/api";
import { RelationalFilter } from "../../utils/filters";
import { IGroupTaskList } from "../types";

type GroupTaskListQueriesParameters = {
    groupSpace: RelationalFilter<IGroupSpaceResource>;
    learners: RelationalFilter<IUserResource>[];
};

/**
 * The group-task collection endpoint requires one learner filter per request.
 * Build one authorized request for each member so callers can present the shared list.
 */
export const groupTaskListQueries = ({ groupSpace, learners }: GroupTaskListQueriesParameters) =>
    learners.flatMap(learner => {
        const query = groupTaskListQuery({
            filters: { groupSpace, learner },
            pagination: false,
        });

        return query ? [query] : [];
    });

export const useGroupTaskListsQuery = (
    parameters: GroupTaskListQueriesParameters,
    { enabled = true }: { enabled?: boolean } = {},
) => {
    const api = useApi();
    const queryConfigs = useMemo(() => groupTaskListQueries(parameters), [parameters.groupSpace, parameters.learners]);
    const queries = useQueries({
        queries: queryConfigs.map(config => ({
            queryKey: ["group-tasks", config.url],
            queryFn: () => unwrapAPIResult<IGroupTaskList>(api.request<IGroupTaskList>(config)),
            enabled,
        })),
    });

    return {
        data: queries.flatMap(query => query.data?.["hydra:member"] ?? []),
        error: queries.find(query => query.error)?.error,
        isLoading: enabled && queries.some(query => query.isLoading),
        refetch: () => Promise.all(queries.map(query => query.refetch())),
    };
};
